// src/components/ctb/ExportacionCtb/ExportacionCtbPresentation.jsx
import React from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  Box,
  Button,
  Typography,
  Chip,
  Paper,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  CloudDownload,
  Settings,
  FileDownload,
  CloudDownloadOutlined
} from '@mui/icons-material';

// Componentes compartidos
import { 
  CardContainer, 
  FilterBar, 
  EmptyState, 
  LoadingState, 
  DataTable, 
  FormField, 
  FormSelect, 
  ConfirmDialog 
} from '../../shared';

/**
 * Componente de presentación para la exportación contable
 * Solo se encarga de la UI, no contiene lógica de negocio
 */
const ExportacionCtbPresentation = ({
  // Datos empresa
  empresaActiva,
  
  // Estados UI
  soloPendientes,
  openParametros,
  
  // Datos
  periodos,
  exportaciones,
  
  // Estados de carga
  loadingExportaciones,
  errorExportaciones,
  loadingParametros,
  guardandoParametros,
  exportando,
  errorExportacion,
  
  // Formularios
  exportacionForm,
  parametrosForm,
  
  // Handlers
  onChangeSoloPendientes,
  onOpenParametros,
  onCloseParametros,
  onFiltrar,
  onLimpiarFiltros,
  onExportarAsientos,
  onGuardarParametros,
  onDescargarArchivo,
  
  // Config tabla
  exportacionesColumns,
  exportacionesActions
}) => {
  // Renderizar estado en tabla de exportaciones
  const renderEstado = (val) => (
    <Chip
      label={val}
      color={
        val === "COMPLETADO" 
          ? "success" 
          : val === "CON_ERRORES" 
            ? "warning" 
            : "error"
      }
      size="small"
    />
  );
  
  // Renderizar acciones en tabla de exportaciones
  const renderAcciones = () => {
    return exportacionesActions.map((action, index) => ({
      ...action,
      icon: <FileDownload fontSize="small" />
    }));
  };
  
  // Botón de configuración para el header de CardContainer
  const actionButton = (
    <Button
      variant="outlined"
      onClick={onOpenParametros}
      startIcon={<Settings />}
      size="small"
    >
      Configuración
    </Button>
  );
  
  return (
    <CardContainer
      title="Exportación Contable"
      subheader="Genere asientos contables a partir de los movimientos RCV y expórtelos a CSV"
      actionComponent={actionButton}
    >
      {/* Barra de filtros */}
      <FilterBar
        onFilter={onFiltrar}
        onReset={onLimpiarFiltros}
        filterButtonText="Cargar exportaciones"
      >
        <Grid item xs={12} md={3}>
          <FormField
            type="text"
            label="RUT Empresa"
            value={`${exportacionForm.values.rutEmpresa}-${exportacionForm.values.dvEmpresa}`}
            disabled={true}
            size="small"
          />
        </Grid>
        
        <Grid item xs={12} md={3}>
          <FormSelect
            name="periodo"
            label="Período"
            value={exportacionForm.values.periodo}
            onChange={exportacionForm.handleChange}
            options={periodos}
            size="small"
          />
        </Grid>
        
        <Grid item xs={12} md={3}>
          <FormControlLabel
            control={
              <Switch
                checked={soloPendientes}
                onChange={onChangeSoloPendientes}
                color="primary"
              />
            }
            label="Solo movimientos pendientes"
          />
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Button
            variant="contained"
            color="secondary"
            onClick={onExportarAsientos}
            disabled={exportando || !empresaActiva || !exportacionForm.values.periodo}
            startIcon={<CloudDownload />}
            fullWidth
          >
            {exportando ? 'Exportando...' : 'Exportar Asientos'}
          </Button>
        </Grid>
      </FilterBar>

      {/* Contenido principal */}
      <LoadingState loading={false} error={errorExportacion || errorExportaciones}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Exportaciones Realizadas
          </Typography>
          
          <LoadingState loading={loadingExportaciones} error={errorExportaciones}>
            {exportaciones && exportaciones.length > 0 ? (
              <DataTable
                columns={exportacionesColumns.map(col => col.field === 'estado' ? {
                  ...col,
                  renderCell: renderEstado
                } : col)}
                data={exportaciones}
                actions={renderAcciones()}
                pagination={true}
                stickyHeader={true}
              />
            ) : (
              <EmptyState
                title="No hay exportaciones"
                message="No se encontraron exportaciones para el período seleccionado."
                usePaper={true}
                icon={<CloudDownloadOutlined style={{ fontSize: 48, color: 'text.secondary', opacity: 0.5 }} />}
              />
            )}
          </LoadingState>
        </Paper>
        
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Notas sobre la exportación contable:
          </Typography>
          <Typography variant="body2" paragraph>
            La exportación contable genera asientos en un formato CSV compatible con el sistema ERP destino.
            Se procesarán automáticamente todos los movimientos que tengan una asociación contable definida.
          </Typography>
          <Typography variant="body2">
            • Para cada movimiento, se genera un asiento contable completo (debe = haber).
          </Typography>
          <Typography variant="body2">
            • Los folios de los asientos se generan automáticamente de forma correlativa.
          </Typography>
          <Typography variant="body2">
            • Para las notas de crédito (tipo documento 61), se invierten automáticamente los debe y haber.
          </Typography>
        </Box>
      </LoadingState>
      
      {/* Diálogo de configuración de parámetros */}
      <ConfirmDialog
        open={openParametros}
        onClose={onCloseParametros}
        onConfirm={onGuardarParametros}
        title="Configuración Contable"
        content={
          <Box sx={{ py: 1 }}>
            <LoadingState 
              loading={loadingParametros} 
              error={null}
              loadingMessage="Cargando parámetros contables..."
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormField
                    type="text"
                    label="Folio Inicial"
                    name="folio_inicial"
                    value={parametrosForm.values.folio_inicial}
                    onChange={parametrosForm.handleChange}
                    disabled={guardandoParametros}
                    helperText="Número de folio inicial para los asientos contables"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <FormField
                    type="text"
                    label="Cuenta IVA"
                    name="cuenta_iva"
                    value={parametrosForm.values.cuenta_iva}
                    onChange={parametrosForm.handleChange}
                    disabled={guardandoParametros}
                    helperText="Cuenta contable para registrar el IVA"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <FormField
                    type="text"
                    label="Cuenta Otros Impuestos"
                    name="cuenta_otros_impuestos"
                    value={parametrosForm.values.cuenta_otros_impuestos}
                    onChange={parametrosForm.handleChange}
                    disabled={guardandoParametros}
                    helperText="Cuenta contable para registrar otros impuestos"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <FormField
                    type="text"
                    label="Cuenta Proveedores"
                    name="cuenta_proveedores"
                    value={parametrosForm.values.cuenta_proveedores}
                    onChange={parametrosForm.handleChange}
                    disabled={guardandoParametros}
                    helperText="Cuenta contable para proveedores"
                  />
                </Grid>
              </Grid>
            </LoadingState>
          </Box>
        }
        loading={guardandoParametros}
        confirmText="Guardar Configuración"
        cancelText="Cancelar"
      />
    </CardContainer>
  );
};

ExportacionCtbPresentation.propTypes = {
  // Datos empresa
  empresaActiva: PropTypes.object,
  
  // Estados UI
  soloPendientes: PropTypes.bool.isRequired,
  openParametros: PropTypes.bool.isRequired,
  
  // Datos
  periodos: PropTypes.array.isRequired,
  exportaciones: PropTypes.array.isRequired,
  
  // Estados de carga
  loadingExportaciones: PropTypes.bool,
  errorExportaciones: PropTypes.string,
  loadingParametros: PropTypes.bool,
  guardandoParametros: PropTypes.bool,
  exportando: PropTypes.bool,
  errorExportacion: PropTypes.string,
  
  // Formularios
  exportacionForm: PropTypes.object.isRequired,
  parametrosForm: PropTypes.object.isRequired,
  
  // Handlers
  onChangeSoloPendientes: PropTypes.func.isRequired,
  onOpenParametros: PropTypes.func.isRequired,
  onCloseParametros: PropTypes.func.isRequired,
  onFiltrar: PropTypes.func.isRequired,
  onLimpiarFiltros: PropTypes.func.isRequired,
  onExportarAsientos: PropTypes.func.isRequired,
  onGuardarParametros: PropTypes.func.isRequired,
  onDescargarArchivo: PropTypes.func.isRequired,
  
  // Config tabla
  exportacionesColumns: PropTypes.array.isRequired,
  exportacionesActions: PropTypes.array.isRequired
};

export default ExportacionCtbPresentation;